import React from 'react';
import _ from 'lodash';

import {
  BaseTier1ReturnAuthorizationModal,
  LabelledText,
  appValidationUtil,
  selectFields
} from 'modules';
import {
  Control,
  useForm
} from 'react-hook-form';
import {
  Box,
  Button,
  Link,
  Stack,
  Typography
} from '@mui/material';
import {
  LoadingOverlay,
  errorUtil,
  fields
} from 'iron-forge-react-modules';

import * as dataService from './dataService';
import * as types from './types';



const {
  genController: genInventoryItemController
} = selectFields.ItemSelectField.genComponentAndControllerGenerator();
const ControlledInventoryItemSelectField = genInventoryItemController<types.IFormState>();

const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const {
  genController: genStoreController
} = selectFields.StoreSelectField.genComponentAndControllerGenerator({
  types: ['cleoTier1']
});
const ControlledStoreSelectField = genStoreController<types.IFormState>();



export interface IProps {
  serialNumber: string;

  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const [lookUp, lookUpTuple] = dataService.useLookUpTier1ReturnAuthorizationByDetails();
  const [receive, receiveTuple] = dataService.useReceiveTier1ReturnAuthorization();
  const loading = lookUpTuple.loading || receiveTuple.loading;

  const [submittedFormState, setSubmittedFormState] = React.useState<types.IFormState>({
    serialNumber: '',
    item: null,
    store: null
  });

  const form = useForm<types.IFormState>({
    defaultValues: {
      item: null,
      serialNumber: props.serialNumber
    }
  });

  const formIsComplete = React.useMemo(
    () => !!submittedFormState.serialNumber
      && !_.isNil(submittedFormState.item)
      && !_.isNil(submittedFormState.store),
    [submittedFormState]
  );

  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      setSubmittedFormState(formState);

      if (formIsComplete && lookUpTuple.data.returnAuthorization && !loading) {
        await errorUtil.errorWrap(async () => {
          if (lookUpTuple.data.returnAuthorization) {
            await receive(formState, lookUpTuple.data.returnAuthorization.id);
            props.onClose();
          }
        }, {});
      }
    },
    [
      formIsComplete,
      loading,
      lookUpTuple.data.returnAuthorization,
      props,
      receive
    ]
  );

  React.useEffect(() => {
    if (submittedFormState.serialNumber && submittedFormState.item && submittedFormState.store) {
      lookUp(submittedFormState);
    }
  }, [
    lookUp,
    submittedFormState
  ]);

  const handleClickPrevious = React.useCallback(
    () => {
      if (submittedFormState.store) {
        setSubmittedFormState({
          ...submittedFormState,
          store: null
        });
        form.setValue('store', null);

      } else if (submittedFormState.item) {
        setSubmittedFormState({
          ...submittedFormState,
          item: null
        });
        form.setValue('item', null);

      } else if (submittedFormState.serialNumber) {
        setSubmittedFormState({
          ...submittedFormState,
          serialNumber: ''
        });

      } else {
        props.onClose();
      }
    },
    [
      form,
      props,
      submittedFormState
    ]
  );



  const handleClickStartOver = React.useCallback(
    () => {
      setSubmittedFormState({
        serialNumber: '',
        item: null,
        store: null
      });
      form.setValue('store', null);
      form.setValue('item', null);
    },
    [form]
  );

  return (
    <>
      <BaseTier1ReturnAuthorizationModal.FC
        title={__getTitle(submittedFormState)}
        primaryText={__getPrimaryText(submittedFormState)}
        secondaryText={__getSecondaryText(submittedFormState)}
        onClose={props.onClose}
        onSubmit={form.handleSubmit(processSubmit)}
        disableButtons={loading}
        overrideCloseButtonArgs={{
          label: submittedFormState.serialNumber ? "Previous" : "Cancel",
          onClick: handleClickPrevious
        }}
        overrideSubmitButtonArgs={{
          label: formIsComplete && lookUpTuple.data.returnAuthorization ? "Receive Item" : "Next"
        }}>
        <Box>
          {__genBodyContents(
            submittedFormState,
            form.control,
            lookUpTuple.data.returnAuthorization
          )}

          {formIsComplete && lookUpTuple.called && !lookUpTuple.loading && _.isNil(lookUpTuple.data.returnAuthorization) && (
            <Stack
              alignItems="center"
              gap={2}>
              <Typography color="error">
                No records found
              </Typography>

              <Button
                variant="outlined"
                onClick={handleClickStartOver}>
                Start Over
              </Button>
            </Stack>
          )}

        </Box>
      </BaseTier1ReturnAuthorizationModal.FC>

      <LoadingOverlay.FC active={lookUpTuple.loading} />
    </>
  );
};



function __genBodyContents(formState: types.IFormState, formControl: Control<types.IFormState>, returnAuthorization: dataService.IReturnAuthorization | null) {
  const hasSerialNumber = !!formState.serialNumber;
  const hasStore = !_.isNil(formState.store);
  const hasInventoryItem = !_.isNil(formState.item);

  const elSerialNumberField = (
    <ControlledTextField
      isDisabled={hasSerialNumber && !hasStore}
      fieldKey="serialNumber"
      formControl={formControl}
      isRequired
      label="IMEI/ICCID"
      extraValidationRules={{
        pattern: appValidationUtil.reImei
      }} />
  );

  if (!hasSerialNumber) {
    return elSerialNumberField;

  } else if (hasSerialNumber && !hasInventoryItem) {
    return (
      <ControlledInventoryItemSelectField
        fieldKey="item"
        formControl={formControl}
        isRequired />
    );

  } else if (hasSerialNumber && hasInventoryItem && !hasStore) {
    return (
      <>
        {elSerialNumberField}

        <ControlledStoreSelectField
          fieldKey="store"
          formControl={formControl}
          label="Tier 1 Partner"
          isRequired />
      </>
    );

  } else {
    return returnAuthorization ? (
      <Stack>
        <LabelledText.FC
          direction="horizontal"
          title="Tier 1 Partner"
          text={<Link>{formState.store?.displayName}</Link>} />

        <LabelledText.FC
          direction="horizontal"
          title="RMA Created on"
          text={returnAuthorization.createdTimestamp.toFormat('MM/dd/yy')} />

        <LabelledText.FC
          direction="horizontal"
          title="RMA Number"
          text={<Link>{returnAuthorization.returnNumber}</Link>} />

        <LabelledText.FC
          direction="horizontal"
          title="RMA Reference Num"
          text={<Link>{returnAuthorization.referenceNumber}</Link>} />

        <LabelledText.FC
          direction="horizontal"
          title="Model Name"
          text={<Link>{returnAuthorization.inventoryItemName}</Link>} />

        <LabelledText.FC
          direction="horizontal"
          title="SKU"
          text={<Link>{returnAuthorization.inventoryItemSku}</Link>} />
      </Stack>
    ) : null;
  }
}



function __getTitle(formState: types.IFormState) {
  if (formState.store) {
    return "SUBMIT RECEIPT";
  } else if (formState.item) {
    return "RECEIVE ITEM";
  } else if (formState.serialNumber) {
    return "RECEIVE ITEM";
  } else if (!formState.serialNumber) {
    return "RECEIVE ITEM";
  }

  return "SHOULD_NOT_HAPPEN";
}



function __getPrimaryText(formState: types.IFormState) {
  if (formState.store) {
    return "Confirm Details";
  } else if (formState.item) {
    return "Tier 1 partner";
  } else if (formState.serialNumber) {
    return "Device Details";
  } else if (!formState.serialNumber) {
    return "Device Details";
  }

  return "SHOULD_NOT_HAPPEN";
}



function __getSecondaryText(formState: types.IFormState) {
  if (formState.store) {
    return "We could not find an RMA with that IMEI. Please confirm receipt details";
  } else if (formState.item) {
    return "We could not find an RMA with that IMEI. Please select the Tier 1 partner.";
  } else if (formState.serialNumber) {
    return "We could not find an RMA with that IMEI.  Please enter the product SKU and select the model.";
  } else if (!formState.serialNumber) {
    return "Enter an IMEI to find the correct RMA.";
  }

  return "SHOULD_NOT_HAPPEN";
}
